import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  Divider,
  Card,
  PageHeader,
} from 'antd';
import { handleApiSuccess, handleApiError } from 'utils';

import {
  rackDeviceCreateService,
  rackDeviceUpdateService,
  rackDeviceGetByIdService,
  getVendorsListService,
  getModelsByVendorService,
  getDevicesByVendorAndModelService,
} from 'services/rackdevice';
import NetworkToolWrapper from '../../layouts/NetworkToolWrapper';
import { MyBreadcrumb } from 'utils';

const { Option } = Select;

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 7 },
};

const tailLayout = {
  wrapperCol: { offset: 5, span: 7 },
};

export default function RackDeviceForm(props) {
  const { id, noWrapper } = props;
  const [form] = Form.useForm();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [totalNics, setTotalNics] = useState(0);
  const [totalPsus, setTotalPsus] = useState(0);
  const [vendors, setVendors] = useState([]);
  const [models, setModels] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const pageTitle = id ? 'Edit Rack Device' : 'Create Rack Device';

  // Load vendors on component mount
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const response = await getVendorsListService();
        console.log('Vendors API Response:', response);
        if (response.data && response.data.items) {
          console.log('Setting vendors:', response.data.items);
          setVendors(response.data.items);
        } else {
          console.log('No vendors data in response');
        }
      } catch (error) {
        console.error('Error loading vendors:', error);
        handleApiError(error);
      }
    };

    console.log('Loading vendors0');
    loadVendors();
  }, []);

  // Add console log to check vendors state
  useEffect(() => {
    console.log('Current vendors state:', vendors);
  }, [vendors]);

  // Load existing data if in edit mode
  useEffect(() => {
    const loadExistingData = async () => {
      if (!id) return;

      setIsLoading(true);
      try {
        const response = await rackDeviceGetByIdService(id);
        const deviceData = response.data.items;

        // Set vendor first to trigger model loading
        if (deviceData.model?.vendor) {
          setSelectedVendor(deviceData.model.vendor);
          // Load models for the selected vendor
          const modelsResponse = await getModelsByVendorService(
            deviceData.model.vendor,
          );
          if (
            modelsResponse.data.items &&
            modelsResponse.data.items.length > 0
          ) {
            setModels(modelsResponse.data.items);

            // Set model to trigger device loading
            if (deviceData.model._id) {
              setSelectedModel(deviceData.model._id);
              // Load devices for the selected model
              const devicesResponse = await getDevicesByVendorAndModelService(
                deviceData.model.vendor,
                deviceData.model._id,
              );
              if (
                devicesResponse.data.items &&
                devicesResponse.data.items.length > 0
              ) {
                setDevices(devicesResponse.data.items);
              }
            }
          }
        }

        // Set form values
        form.setFieldsValue({
          vendor: deviceData.model?.vendor,
          model: deviceData.model?._id,
          deviceregistry: deviceData.deviceregistry?._id,
          equipment_name: deviceData.equipment_name,
          lock_code: deviceData.lock_code,
          labeled_as: deviceData.labeled_as,
          serial_number: deviceData.serial_number,
          service_tag: deviceData.service_tag,
          number_of_u: deviceData.number_of_u,
          reuse_option: deviceData.reuse_option,
          status: deviceData.status,
          primary_use: deviceData.primary_use,
          equipment_alert: deviceData.equipment_alert,
          total_psus: deviceData.total_psus,
          total_nics: deviceData.total_nics,
          equipment_notes: deviceData.equipment_notes,
          psu_details: deviceData.psu_details,
          nic_details: deviceData.nic_details,
        });

        setTotalPsus(deviceData.total_psus || 0);
        setTotalNics(deviceData.total_nics || 0);
      } catch (error) {
        console.error('Error loading existing data:', error);
        handleApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingData();
  }, [id, form]);

  const handleVendorChange = async (vendorId) => {
    console.log('Vendor changed to:', vendorId);
    setSelectedVendor(vendorId);
    setSelectedModel(null);
    setModels([]);
    setDevices([]);

    // Clear dependent fields
    form.setFieldsValue({
      model: undefined,
      deviceregistry: undefined,
    });

    if (vendorId) {
      try {
        const response = await getModelsByVendorService(vendorId);
        console.log('Models API Response:', response);
        if (response.data && response.data.items) {
          console.log('Setting models:', response.data.items);
          setModels(response.data.items);
        } else {
          console.log('No models data in response');
        }
      } catch (error) {
        console.error('Error loading models:', error);
        handleApiError(error);
      }
    }
  };

  const handleModelChange = async (modelId) => {
    console.log('Model changed to:', modelId);
    setSelectedModel(modelId);
    setDevices([]);

    // Clear dependent field
    form.setFieldsValue({
      deviceregistry: undefined,
    });

    if (modelId && selectedVendor) {
      try {
        const response = await getDevicesByVendorAndModelService(
          selectedVendor,
          modelId,
        );
        console.log('Devices API Response:', response);
        if (response.data && response.data.items) {
          console.log('Setting devices:', response.data.items);
          setDevices(response.data.items);
        } else {
          console.log('No devices data in response');
        }
      } catch (error) {
        console.error('Error loading devices:', error);
        handleApiError(error);
      }
    }
  };

  const handleTotalNicsChange = (value) => {
    setTotalNics(value || 0);
  };

  const handleTotalPsusChange = (value) => {
    setTotalPsus(value || 0);
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      if (id) {
        await rackDeviceUpdateService(id, values);
        handleApiSuccess('Rack device updated successfully');
      } else {
        await rackDeviceCreateService(values);
        handleApiSuccess('Rack device created successfully');
      }
      history.push('/rackdevice');
    } catch (error) {
      console.error('Error saving rack device:', error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderNicDetails = () => {
    return [...Array(totalNics)].map((_, index) => (
      <Card key={index} style={{ marginBottom: 16 }}>
        <h3>NIC {index + 1}</h3>
        <Form.Item label="Port Type" name={['nic_details', index, 'port_type']}>
          <Select placeholder="Select port type">
            <Option value="Ethernet">Ethernet</Option>
            <Option value="Fiber">Fiber</Option>
            <Option value="SFP">SFP</Option>
            <Option value="SFP+">SFP+</Option>
            <Option value="QSFP">QSFP</Option>
            <Option value="QSFP+">QSFP+</Option>
            <Option value="QSFP28">QSFP28</Option>
            <Option value="QSFP56">QSFP56</Option>
            <Option value="QSFP112">QSFP112</Option>
            <Option value="DAC">DAC</Option>
            <Option value="AOC">AOC</Option>
            <Option value="Twinax">Twinax</Option>
            <Option value="Copper">Copper</Option>
            <Option value="Other">Other</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Speed" name={['nic_details', index, 'speed']}>
          <Select placeholder="Select speed">
            <Option value="10Mbps">10 Mbps</Option>
            <Option value="100Mbps">100 Mbps</Option>
            <Option value="1Gbps">1 Gbps</Option>
            <Option value="2.5Gbps">2.5 Gbps</Option>
            <Option value="5Gbps">5 Gbps</Option>
            <Option value="10Gbps">10 Gbps</Option>
            <Option value="25Gbps">25 Gbps</Option>
            <Option value="40Gbps">40 Gbps</Option>
            <Option value="50Gbps">50 Gbps</Option>
            <Option value="100Gbps">100 Gbps</Option>
            <Option value="200Gbps">200 Gbps</Option>
            <Option value="400Gbps">400 Gbps</Option>
            <Option value="800Gbps">800 Gbps</Option>
            <Option value="1.6Tbps">1.6 Tbps</Option>
            <Option value="Other">Other</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Notes" name={['nic_details', index, 'notes']}>
          <Input.TextArea
            rows={2}
            placeholder="e.g., Port 1/1/1 or Connected to Switch A"
          />
        </Form.Item>
      </Card>
    ));
  };

  // Simple vendor dropdown with search
  const renderVendorDropdown = () => (
    <Form.Item
      label="Vendor"
      name="vendor"
      id="vendorSelect"
      rules={[{ required: true, message: 'Please select a vendor' }]}
    >
      <Select
        onChange={handleVendorChange}
        onKeyDown={(e) => {
          const listId = e.target.getAttribute('aria-controls');
          const { key } = e;
          if (key === 'Tab') {
            const dropdown = document
              .getElementById(listId)
              .parentElement.querySelector('.ant-select-item-option-active');
            if (dropdown) {
              dropdown.click();
            }
          }
        }}
        placeholder="Search and select a vendor"
        loading={isLoading}
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {vendors && vendors.length > 0 ? (
          vendors.map((vendor) => (
            <Option key={vendor._id} value={vendor._id}>
              {vendor.name}
            </Option>
          ))
        ) : (
          <Option disabled>No vendors available</Option>
        )}
      </Select>
    </Form.Item>
  );

  // Simple model dropdown with search
  const renderModelDropdown = () => (
    <Form.Item
      label="Model"
      name="model"
      rules={[{ required: true, message: 'Please select a model' }]}
    >
      <Select
        onChange={handleModelChange}
        onKeyDown={(e) => {
          const listId = e.target.getAttribute('aria-controls');
          const { key } = e;
          if (key === 'Tab') {
            const dropdown = document
              .getElementById(listId)
              .parentElement.querySelector('.ant-select-item-option-active');
            if (dropdown) {
              dropdown.click();
            }
          }
        }}
        disabled={!selectedVendor}
        placeholder="Search and select a model"
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {models.map((model) => (
          <Option key={model._id} value={model._id}>
            {model.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  // Simple device registry dropdown with search
  const renderDeviceRegistryDropdown = () => (
    <Form.Item label="Device Registry" name="deviceregistry">
      <Select
        onKeyDown={(e) => {
          const listId = e.target.getAttribute('aria-controls');
          const { key } = e;
          if (key === 'Tab') {
            const dropdown = document
              .getElementById(listId)
              .parentElement.querySelector('.ant-select-item-option-active');
            if (dropdown) {
              dropdown.click();
            }
          }
        }}
        disabled={!selectedModel}
        placeholder="Search and select a device"
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {devices.map((device) => (
          <Option key={device._id} value={device._id}>
            {device.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );

  if (noWrapper) {
    return (
      <Form
        name="rackDeviceForm"
        form={form}
        {...layout}
        onFinish={onFinish}
        disabled={isLoading}
      >
        <Space>&nbsp;</Space>
        {renderVendorDropdown()}
        {renderModelDropdown()}
        {renderDeviceRegistryDropdown()}
        <Form.Item
          label="Equipment Name"
          name="equipment_name"
          rules={[{ required: true, message: 'Please input equipment name' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Lock Code" name="lock_code">
          <Input />
        </Form.Item>
        <Form.Item label="Labeled As" name="labeled_as">
          <Input />
        </Form.Item>
        <Form.Item label="Serial Number" name="serial_number">
          <Input />
        </Form.Item>
        <Form.Item label="Service Tag" name="service_tag">
          <Input />
        </Form.Item>
        <Form.Item
          label="Number of U"
          name="number_of_u"
          rules={[{ required: true, message: 'Please select number of U' }]}
        >
          <Select
            placeholder="Search and select number of U"
            onKeyDown={(e) => {
              const listId = e.target.getAttribute('aria-controls');
              const { key } = e;
              if (key === 'Tab') {
                const dropdown = document
                  .getElementById(listId)
                  .parentElement.querySelector(
                    '.ant-select-item-option-active',
                  );
                if (dropdown) {
                  dropdown.click();
                }
              }
            }}
            showSearch
            filterOption={(input, option) =>
              String(option.children)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            {[...Array(20)].map((_, index) => (
              <Option key={index + 1} value={index + 1}>
                {index + 1}U
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Reuse Option"
          name="reuse_option"
          rules={[{ required: true, message: 'Please select reuse option' }]}
        >
          <Select
            onKeyDown={(e) => {
              const listId = e.target.getAttribute('aria-controls');
              const { key } = e;
              if (key === 'Tab') {
                const dropdown = document
                  .getElementById(listId)
                  .parentElement.querySelector(
                    '.ant-select-item-option-active',
                  );
                if (dropdown) {
                  dropdown.click();
                }
              }
            }}
            placeholder="Search and select reuse option"
            showSearch
            filterOption={(input, option) =>
              String(option.children)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option value="Yes">Yes - Device can be reused</Option>
            <Option value="No">No - Device can only be used once</Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: 'Please select status' }]}
        >
          <Select
            placeholder="Search and select status"
            showSearch
            filterOption={(input, option) =>
              String(option.children)
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            onKeyDown={(e) => {
              const listId = e.target.getAttribute('aria-controls');
              const { key } = e;
              if (key === 'Tab') {
                const dropdown = document
                  .getElementById(listId)
                  .parentElement.querySelector(
                    '.ant-select-item-option-active',
                  );
                if (dropdown) {
                  dropdown.click();
                }
              }
            }}
          >
            <Option value="Active">Active</Option>
            <Option value="Inactive">Inactive</Option>
            <Option value="Active Primary">Active Primary</Option>
            <Option value="Active Backup">Active Backup</Option>
            <Option value="Spare">Spare</Option>
            <Option value="Broken">Broken</Option>
            <Option value="Removed">Removed</Option>
            <Option value="Retired">Retired</Option>
            <Option value="To Be Installed">To Be Installed</Option>
            <Option value="To Be Removed">To Be Removed</Option>
            <Option value="To Be Retired">To Be Retired</Option>
            <Option value="Future">Future</Option>
          </Select>
        </Form.Item>
        <Form.Item label="Primary Use" name="primary_use">
          <Input />
        </Form.Item>
        <Form.Item label="Equipment Alert" name="equipment_alert">
          <Input />
        </Form.Item>
        <Form.Item label="Total PSUs" name="total_psus">
          <InputNumber min={0} max={10} onChange={handleTotalPsusChange} />
        </Form.Item>
        {totalPsus > 0 && (
          <>
            <Divider orientation="left">PSU Details</Divider>
            {[...Array(totalPsus)].map((_, index) => (
              <Card key={index} style={{ marginBottom: 16 }}>
                <h3>PSU {index + 1}</h3>
                <Form.Item label="Notes" name={['psu_details', index, 'notes']}>
                  <Input.TextArea
                    rows={2}
                    placeholder="e.g., Power Bank A or To Client UPS"
                  />
                </Form.Item>
              </Card>
            ))}
          </>
        )}
        <Form.Item label="Total NICs" name="total_nics">
          <InputNumber min={0} max={10} onChange={handleTotalNicsChange} />
        </Form.Item>
        {totalNics > 0 && (
          <>
            <Divider orientation="left">NIC Details</Divider>
            {renderNicDetails()}
          </>
        )}
        <Form.Item label="Equipment Notes" name="equipment_notes">
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item {...tailLayout}>
          <Space>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {id ? 'Update' : 'Create'}
            </Button>
            <Button onClick={() => history.push('/rackdevice')}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    );
  }

  return (
    <NetworkToolWrapper isBranchClientRequired={false}>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Networking Dashboard', path: '/network-dashboard' },
          { label: 'Rack Devices', path: '/rackdevice' },
          { label: pageTitle, path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/rackdevice')}
        title={pageTitle}
      >
        <Form
          name="rackDeviceForm"
          form={form}
          {...layout}
          onFinish={onFinish}
          disabled={isLoading}
        >
          <Space>&nbsp;</Space>

          {renderVendorDropdown()}
          {renderModelDropdown()}
          {renderDeviceRegistryDropdown()}

          <Form.Item
            label="Equipment Name"
            name="equipment_name"
            rules={[{ required: true, message: 'Please input equipment name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Lock Code" name="lock_code">
            <Input />
          </Form.Item>

          <Form.Item label="Labeled As" name="labeled_as">
            <Input />
          </Form.Item>

          <Form.Item label="Serial Number" name="serial_number">
            <Input />
          </Form.Item>

          <Form.Item label="Service Tag" name="service_tag">
            <Input />
          </Form.Item>

          <Form.Item
            label="Number of U"
            name="number_of_u"
            rules={[{ required: true, message: 'Please select number of U' }]}
          >
            <Select
              placeholder="Search and select number of U"
              showSearch
              filterOption={(input, option) =>
                String(option.children)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {[...Array(20)].map((_, index) => (
                <Option key={index + 1} value={index + 1}>
                  {index + 1}U
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Reuse Option"
            name="reuse_option"
            rules={[{ required: true, message: 'Please select reuse option' }]}
          >
            <Select
              placeholder="Search and select reuse option"
              showSearch
              filterOption={(input, option) =>
                String(option.children)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="Yes">Yes - Device can be reused</Option>
              <Option value="No">No - Device can only be used once</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select
              placeholder="Search and select status"
              showSearch
              filterOption={(input, option) =>
                String(option.children)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
              <Option value="Active Primary">Active Primary</Option>
              <Option value="Active Backup">Active Backup</Option>
              <Option value="Spare">Spare</Option>
              <Option value="Broken">Broken</Option>
              <Option value="Removed">Removed</Option>
              <Option value="Retired">Retired</Option>
              <Option value="To Be Installed">To Be Installed</Option>
              <Option value="To Be Removed">To Be Removed</Option>
              <Option value="To Be Retired">To Be Retired</Option>
              <Option value="Future">Future</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Primary Use" name="primary_use">
            <Input />
          </Form.Item>

          <Form.Item label="Equipment Alert" name="equipment_alert">
            <Input />
          </Form.Item>

          <Form.Item label="Total PSUs" name="total_psus">
            <InputNumber min={0} max={10} onChange={handleTotalPsusChange} />
          </Form.Item>

          {totalPsus > 0 && (
            <>
              <Divider orientation="left">PSU Details</Divider>
              {[...Array(totalPsus)].map((_, index) => (
                <Card key={index} style={{ marginBottom: 16 }}>
                  <h3>PSU {index + 1}</h3>
                  <Form.Item
                    label="Notes"
                    name={['psu_details', index, 'notes']}
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder="e.g., Power Bank A or To Client UPS"
                    />
                  </Form.Item>
                </Card>
              ))}
            </>
          )}

          <Form.Item label="Total NICs" name="total_nics">
            <InputNumber min={0} max={10} onChange={handleTotalNicsChange} />
          </Form.Item>

          {totalNics > 0 && (
            <>
              <Divider orientation="left">NIC Details</Divider>
              {renderNicDetails()}
            </>
          )}

          <Form.Item label="Equipment Notes" name="equipment_notes">
            <Input.TextArea rows={4} />
          </Form.Item>

          <Form.Item {...tailLayout}>
            <Space>
              <Button type="primary" htmlType="submit" loading={isLoading}>
                {id ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => history.push('/rackdevice')}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </PageHeader>
    </NetworkToolWrapper>
  );
}
